import { LoadingButton } from "@mui/lab";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Contact } from "@repo/server-api";
import { EmployeeWorkingConditionInformationType } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect, useRef, useMemo } from "react";

import { validErrorState } from "@repo/legacy/components/helper/Index";

import WorkingConditioins from "../additionalInformation/WorkingConditions";

export default function DialogWorkingCondition(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		workingCondition: EmployeeWorkingConditionInformationType | null;
		setWorkingCondition: (value: EmployeeWorkingConditionInformationType | null) => void;
		handleSubmit: (info: EmployeeWorkingConditionInformationType) => void;
		contact: Contact | null;
		setContact: (value: Contact | null) => void;
	}>,
) {
	const { t } = useTranslation();

	const open = useMemo<boolean>(() => props.open, [props.open]);
	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const [info, setInfo] = useState<EmployeeWorkingConditionInformationType>({
		key: Math.random() * 1000,
		id: "",
	} as unknown as EmployeeWorkingConditionInformationType);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const workingConditionsRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);

	useEffect(() => {
		if (info.id !== props.workingCondition?.id && isLoaded) {
			props.setWorkingCondition(info);
		}
	}, [info, isLoaded]);

	useEffect(() => {
		// TODO - fix this loading issue fix is id = new
		if (
			props.workingCondition?.id !== info.id ||
			props.workingCondition?.id === "new" ||
			props.workingCondition?.key
		) {
			setInfo({
				...(props.workingCondition ?? {
					id:
						props.workingCondition?.id === "new" // es-lint-disable-line
							? ""
							: props.workingCondition?.id, // es-lint-disable-line
					__typename: "EmployeeWorkingConditionInformation",
					from: "",
					to: "",
					workingTimeModel: props.workingCondition?.workingTimeModel ?? "", // es-lint-disable-line
					workingTimeModelId: props.workingCondition?.workingTimeModelId ?? "", // es-lint-disable-line
					federalLocation: "",
					key: (Math.random() * 1000).toString() as string,
				}),
			} as EmployeeWorkingConditionInformationType);
			setIsLoaded(true);
		}
	}, [props.workingCondition, isLoaded, info.id]);

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};

		localErrors.workingConditions = (await workingConditionsRef.current?.validate()) ?? {};

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const isValid = await validateFields();

		if (isValid) {
			setSaveClicked(true);
			props.handleSubmit(info);
			handleClose();
		}
	};

	const resetInfo = () => {
		setInfo({
			id: "",
			__typename: "EmployeeWorkingConditionInformation",
			from: "",
			workingTimeModelId: "",
			federalLocation: "",
			key: Math.random() * 1000,
		} as unknown as EmployeeWorkingConditionInformationType);
	};

	const handleClose = () => {
		props.setOpen(false);
		setIsLoaded(false);
		setErrors({});
		resetInfo();
		setSaveClicked(false);
	};

	return (
		<Dialog
			open={open}
			PaperProps={{
				noValidate: true,
				autoComplete: "off",
				component: "form",
				onSubmit: handleSubmit,
			}}
			maxWidth="sm"
			fullWidth={true}>
			<DialogTitle>
				{info.id ? t("edit-working-condition") : t("new-working-condition")}
			</DialogTitle>
			<DialogContent className="flex-col gap-5">
				<div className="w-100">
					<WorkingConditioins
						ref={workingConditionsRef}
						contact={props.contact}
						setContact={props.setContact}
						setErrors={(value: Record<string, boolean | object | string>) =>
							setErrors({ ...errors, workingConditions: { ...value } })
						}
						errors={errors.workingConditions as Record<string, boolean | object | string>}
						info={info}
						setInfo={setInfo}
					/>
				</div>
			</DialogContent>
			<DialogActions>
				<Button data-testid="close-button" variant="outlined" onClick={handleClose}>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					type="submit"
					variant="contained"
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
